<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { useVariantStock, useCustomerOrderAbility } from '@storefront/storefront';
  import { closestMultipleOf, CustomerType } from '@othercommerce/domain';
  import type { ProductVariantPickerResource } from '@storefront/resources';
  import { useAuthenticated, useTranslator } from '@othercommerce/foundation';
  import { Features } from '@shared/features';

  interface Props {
    variant: ProductVariantPickerResource;
    disabled?: boolean;
  }

  const emit = defineEmits([
    'added',
  ]);

  const props = defineProps<Props>();

  const user = useAuthenticated();
  const orderable = useCustomerOrderAbility();
  const translator = useTranslator();
  const amount = ref(+props.variant.stockUnitBase);

  const { available, amountAvailable } = useVariantStock(computed(() => props.variant));

  const isMedicinalProductPurchasable = computed(() => {
    if (Features.medicines.isEnabled && Features.medicalContractorsRegistry.isEnabled) {
      if (props.variant.isMedicinalProduct) {
        return !!user.value && user.value.type === CustomerType.MEDICAL;
      }
    }

    return true;
  });

  const isMedicinalProductRequestable = computed(() => {
    if (Features.medicines.isEnabled && Features.medicalContractorsRegistry.isDisabled) {
      return props.variant.isMedicinalProduct;
    }

    return true;
  });

  const status = computed(() => {
    if (!isMedicinalProductPurchasable.value) {
      return translator.trans('SHOP.CART.MESSAGE.MEDICINAL_PRODUCT_ERROR');
    }

    if (Features.medicines.isEnabled && Features.medicalContractorsRegistry.isDisabled) {
      if (isMedicinalProductRequestable.value && !user.value) {
        return translator.trans('SHOP.CART.MESSAGE.AUTHENTICATE_TO_REQUEST_PRODUCT');
      }

      if (isMedicinalProductRequestable.value && available.value) {
        return translator.trans('SHOP.CART.MESSAGE.REQUEST_PRODUCT_FOR_DETAILS');
      }
    }

    if (!available.value) {
      return translator.trans('SHOP.CART.MESSAGE.PRODUCT_NOT_AVAILABLE');
    }

    if (!orderable.value) {
      return translator.trans('SHOP.CART.MESSAGE.AUTHENTICATE_TO_PURCHASE');
    }

    return true;
  });

  const disabled = computed(() => {
    return status.value !== true || props.disabled;
  });

  const showRequestButton = computed(() => {
    if (status.value !== true) {
      if (Features.medicines.isEnabled && Features.medicalContractorsRegistry.isEnabled) {
        if (!available.value && isMedicinalProductPurchasable.value) return true;
      }

      if (Features.medicines.isEnabled && Features.medicalContractorsRegistry.isDisabled) {
        if (isMedicinalProductRequestable.value && !!user.value) return true;
        if (isMedicinalProductRequestable.value && !user.value) return false;
        if (!isMedicinalProductRequestable.value && !available.value) return true;
      }

      return !available.value;
    }

    return false;
  });

  function findClosestMultipleOf() {
    amount.value = closestMultipleOf(amount.value, props.variant.stockUnitBase);
  }

  watch(() => props.variant, (variant) => {
    amount.value = +variant.stockUnitBase;
  });
</script>
<template>
  <div class="cart-store-button">
    <div class="cart-store-button__button">
      <FormController v-slot="{ processing, submit }" :action="$route('web.cart.item.store')" :data="{ variant: variant.id, amount }">
        <template v-if="status === true">
          <SfFormNumber
            v-model="amount"
            :disabled
            :label="$t('SHOP.COMMON.LABEL.QUANTITY')"
            :max="amountAvailable"
            :min="variant.stockUnitBase"
            :step="variant.stockUnitBase"
            required
            @change="findClosestMultipleOf"
          />
          <SfButton :aria-controls="variant ? 'cart-success-modal' : null" :disabled :loading="processing" aria-haspopup="dialog" class="btn" primary @click="submit">
            <template v-if="$slots.before" #before>
              <slot name="before" />
            </template>
            <template #default>
              {{ variant ? $t('SHOP.COMMON.ACTION.ADD_TO_CART') : $t('SHOP.COMMON.ACTION.CHOOSE_OPTION') }}
            </template>
            <template v-if="$slots.after" #after>
              <slot name="after" />
            </template>
          </SfButton>
        </template>
        <div v-else class="cart-store-button__status" role="alert" v-html="status" />

        <SfButton v-if="showRequestButton" :href="$route('web.product.request.create', { slug: variant.slug })" primary>
          {{ $t('SHOP.COMMON.ACTION.ASK_FOR_PRODUCT') }}
        </SfButton>
      </FormController>
    </div>
  </div>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-cart-store-button-flex-direction: column;
    --sf-cart-store-button-gap: 1rem;
  }

  .cart-store-button {
    display: flex;
    flex-direction: column;

    &__status {
      color: var(--sf-status-color-danger);
      background: var(--sf-status-background-danger);
      border: 1px solid var(--sf-status-color-danger);
      border-radius: var(--border-radius);
      text-align: center;
      font-weight: var(--font-weight-bold);
      font-size: 0.75rem;
      padding: 0.75rem 0.25rem;
    }

    &__button {
      display: flex;
      flex-direction: var(--sf-cart-store-button-flex-direction);
      gap: var(--sf-cart-store-button-gap);

      .control__wrapper {
        flex: 0 1;
      }

      .btn {
        flex: 1 0;
      }
    }

    &__error {
      color: var(--sf-input-error-color);
      font-size: var(--sf-input-error-font-size);
      padding: var(--sf-input-error-padding-y) var(--sf-input-error-padding-x);
      scroll-margin-top: var(--sf-input-error-scroll-margin-top);
      scroll-margin-bottom: var(--sf-input-error-scroll-margin-bottom);
    }
  }

  @include media.query(md) {
    .cart-store-button__button {
      flex-direction: column;
    }
  }
</style>
